import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box, Typography, Button, CircularProgress, Paper, TextField,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Alert, Chip,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DownloadIcon from "@mui/icons-material/Download";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import { axiosConfig } from "../../axios/axiosConfig";
import toast from "react-hot-toast";
import PaginationControls from "../Pagination/PaginationControls";

const PAGE_SIZE = 50;
const UserActivityPage = () => {
  const navigate = useNavigate();

  // Download states
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [activeLoading, setActiveLoading] = useState(false);

  // Filter states
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [appliedFrom, setAppliedFrom] = useState("");
  const [appliedTo, setAppliedTo] = useState("");

  // Table states
  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [tableLoading, setTableLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    setPage(1);
  }, [appliedFrom, appliedTo]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setTableLoading(true);
        setError(null);
        const params = new URLSearchParams();
        if (appliedFrom) params.append("from_date", appliedFrom);
        if (appliedTo) params.append("to_date", appliedTo);
        params.append("page", page);
        const res = await axiosConfig({ method: "get", url: `/investors/active-user-data/?${params}` });
        setTotalCount(res?.data?.count || 0);
        setRows(res?.data?.results || []);
      } catch (e) {
        setError("Failed to fetch data. Please try again.");
      } finally {
        setTableLoading(false);
      }
    };
    fetchData();
  }, [appliedFrom, appliedTo, page]);

  const handleFilter = () => {
    setAppliedFrom(fromDate);
    setAppliedTo(toDate);
  };

  const download = async (url, fallback, setLoad) => {
    setLoad(true);
    try {
      const params = new URLSearchParams();
      if (fromDate) params.append("from_date", fromDate);
      if (toDate) params.append("to_date", toDate);
      const fullUrl = params.toString() ? `${url}?${params}` : url;
      const res = await axiosConfig({ method: "get", url: fullUrl, responseType: "blob" });
      const disposition = res.headers["content-disposition"] || "";
      const match = disposition.match(/filename[^;=\n]*=["']?([^"';\n]+)/i);
      const filename = (match?.[1]?.trim() || fallback).toLowerCase();
      const blobUrl = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
      toast.success("Download started!");
    } catch (e) {
      toast.error(e.response?.data?.detail || e.response?.data?.message || e.message || "Download failed");
    } finally {
      setLoad(false);
    }
  };

  const fieldSx = {
    width: 150,
    "& .MuiOutlinedInput-root": { borderRadius: "8px", fontSize: 13, background: "#fff" },
    "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e2e8f0" },
  };

  return (
    <Box sx={{ background: "#f1f5f9", minHeight: "100vh" }}>
      {/* Sticky Header */}
      <Box sx={{ position: "sticky", top: 0, zIndex: 100, background: "#fff", borderBottom: "1px solid #e2e8f0", px: 3, py: 2, display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Box
            onClick={() => navigate("/investor")}
            sx={{ width: 34, height: 34, borderRadius: "8px", border: "1px solid #e2e8f0", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", "&:hover": { background: "#f1f5f9" } }}
          >
            <ArrowBackIcon sx={{ fontSize: 18, color: "#64748b" }} />
          </Box>
          <Box sx={{ width: 38, height: 38, borderRadius: "10px", background: "#04343a", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <PeopleAltIcon sx={{ color: "#fff", fontSize: 20 }} />
          </Box>
          <Box>
            <Typography sx={{ fontSize: 20, fontWeight: 800, color: "#0f172a", lineHeight: 1.2 }}>User Activity</Typography>
            <Typography sx={{ fontSize: 12, color: "#94a3b8" }}>Active user data with download reports</Typography>
          </Box>
        </Box>

        {/* Date filter + Downloads */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
          <TextField
            type="date" size="small" label="From" value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={fieldSx}
          />
          <TextField
            type="date" size="small" label="To" value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={fieldSx}
          />
          {/* <Button
            variant="contained" size="small"
            startIcon={<FilterAltIcon sx={{ fontSize: 16 }} />}
            onClick={handleFilter}
            sx={{ height: 36, borderRadius: "8px", background: "#04343a", textTransform: "none", fontSize: 13, fontWeight: 600, px: 2, "&:hover": { background: "#016551" } }}
          >
            Filter
          </Button> */}

          <Button
            variant="contained" size="small"
            disabled={activeLoading}
            startIcon={activeLoading ? <CircularProgress size={14} sx={{ color: "#fff" }} /> : <DownloadIcon sx={{ fontSize: 16 }} />}
            onClick={() => download("/investors/active-user-csv-download/", "user_record.csv", setActiveLoading)}
            sx={{ height: 36, borderRadius: "8px", background: "#04343a", textTransform: "none", fontSize: 13, fontWeight: 600, px: 2, whiteSpace: "nowrap", boxShadow: "0 4px 12px rgba(4,52,58,0.3)", "&:hover": { background: "linear-gradient(135deg,#1db954,#17a349)" } }}
          >
            {activeLoading ? "Downloading…" : "Download User Record"}
          </Button>
                    <Button
            variant="contained" size="small"
            disabled={paymentLoading}
            startIcon={paymentLoading ? <CircularProgress size={14} sx={{ color: "#fff" }} /> : <DownloadIcon sx={{ fontSize: 16 }} />}
            onClick={() => download("/investors/user-payment-csv-download/", "payment_history.csv", setPaymentLoading)}
            sx={{ height: 36, borderRadius: "8px", background: "#016551", textTransform: "none", fontSize: 13, fontWeight: 600, px: 2, whiteSpace: "nowrap", boxShadow: "0 4px 12px rgba(1,101,81,0.3)", "&:hover": { background: "linear-gradient(135deg,#1db954,#17a349)" } }}
          >
            {paymentLoading ? "Downloading…" : "Download Payment History"}
          </Button>
        </Box>
      </Box>

      <Box sx={{ p: 3, display: "flex", flexDirection: "column", gap: 2 }}>

        {error && <Alert severity="error" sx={{ borderRadius: "10px" }}>{error}</Alert>}

        {/* Active user data table */}
        <Paper elevation={0} sx={{ borderRadius: "14px", border: "1px solid #e2e8f0", background: "#fff", boxShadow: "0 1px 6px rgba(0,0,0,0.06)", overflow: "hidden" }}>
          <Box sx={{ px: 2.5, py: 1.8, background: "#04343a", borderRadius: "14px 14px 0 0", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#fff", letterSpacing: 0.5 }}>ACTIVE USER DATA</Typography>
            {!tableLoading && (
              <Typography sx={{ fontSize: 12, color: "rgba(255,255,255,0.55)" }}>
                {totalCount.toLocaleString("en-IN")} records
              </Typography>
            )}
          </Box>

          <TableContainer sx={{ overflowX: "auto", maxHeight: "calc(100vh - 320px)" }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {["#", "Phone", "Username", "Email", "7d", "15d", "30d", "90d", "150d"].map((h) => (
                    <TableCell key={h} sx={{ fontSize: 11, fontWeight: 700, color: "#64748b", py: 1.2, whiteSpace: "nowrap", background: "#f8fafc" }}>
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {tableLoading ? (
                  <TableRow>
                    <TableCell colSpan={9} sx={{ textAlign: "center", py: 6 }}>
                      <CircularProgress size={28} sx={{ color: "#04343a" }} />
                    </TableCell>
                  </TableRow>
                ) : rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} sx={{ textAlign: "center", py: 5, fontSize: 13, color: "#94a3b8" }}>
                      No data found
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((row, i) => (
                    <TableRow key={i} sx={{ "&:hover": { background: "#f8fafc" } }}>
                      <TableCell sx={{ fontSize: 12, color: "#64748b" }}>{(page - 1) * PAGE_SIZE + i + 1}</TableCell>
                      <TableCell sx={{ fontSize: 12, fontWeight: 600, color: "#0f172a" }}>{row.phone || "—"}</TableCell>
                      <TableCell sx={{ fontSize: 12, color: "#475569" }}>{row.username || "—"}</TableCell>
                      <TableCell sx={{ fontSize: 11, color: "#64748b" }}>{row.email || "—"}</TableCell>
                      {["active_7d", "active_15d", "active_30d", "active_90d", "active_150d"].map((key) => (
                        <TableCell key={key}>
                          <Chip
                            label={row[key] ? "Yes" : "No"}
                            size="small"
                            sx={{
                              fontSize: 10, fontWeight: 700, height: 20,
                              background: row[key] ? "#dcfce7" : "#fee2e2",
                              color: row[key] ? "#16a34a" : "#dc2626",
                            }}
                          />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {!tableLoading && totalCount > 0 && (
            <PaginationControls totalCount={totalCount} page={page} setPage={setPage} offset={PAGE_SIZE} />
          )}
        </Paper>
      </Box>
    </Box>
  );
};

export default UserActivityPage;
